import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  hasActiveSubscription: boolean;
  loading: boolean;
  subscriptionData: any | null;
  handleManageSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const handleManageSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      
      // Check if we're using localhost or production
      let API_BASE = process.env.REACT_APP_API_URL || 'https://quick-cover-letter.onrender.com';
      
      // Remove trailing /api if it exists to prevent double /api/api/ prefixing
      if (API_BASE.endsWith('/api')) {
        API_BASE = API_BASE.slice(0, -4); // Remove the trailing /api
      }
      
      // First check if we have a subscription
      const hasSubscription = subscriptionData !== null;
      
      // Determine which endpoint to use - checkout for new subscriptions, portal for existing ones
      const endpoint = hasSubscription ? 'create-portal-session' : 'create-checkout-session';
      
      
      // Make the proper API call with single /api prefix
      let response = await fetch(`${API_BASE}/api/stripe/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          user_id: session.user.id,
          email: session.user.email,
          return_url: window.location.origin + '/account'
        })
      });

      // If that fails, try directly with the backend URL
      if (!response.ok) {
        // Try with the render.com URL directly
        response = await fetch(`https://quick-cover-letter.onrender.com/api/stripe/${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            user_id: session.user.id,
            email: session.user.email,
            return_url: window.location.origin + '/account'
          })
        });
        
        if (!response.ok) {
          const errorMessage = hasSubscription ? 
            'Failed to create portal session' : 
            'Failed to create checkout session';
          throw new Error(errorMessage);
        }
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error handling subscription:', error);
      alert('Error accessing subscription services. Please try again.');
    }
  };
  const { user } = useAuth();
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to notify Chrome extension of subscription state
  const notifyChromeExtension = async (hasActiveSubscription: boolean) => {
    // First check if chrome and chrome.runtime exist
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      return;
    }

    try {
      // Get extension ID from manifest.json
      const extensionId = 'hlbgkfakggdmhmiciglgamccjcgjkmnh';
      
      // Send message to extension
      await chrome.runtime.sendMessage(
        extensionId,
        { 
          type: 'SUBSCRIPTION_STATE_CHANGE',
          hasActiveSubscription
        },
        (response) => {
          if (chrome.runtime.lastError) {
            return;
          }
        }
      );
    } catch (error) {
    }
  };

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        setHasActiveSubscription(false);
        setSubscriptionData(null);
        setLoading(false);
        return;
      }

      try {
        // First check if user has any subscription
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (subError) {
          if (subError.code === '406') {
          } else {
            console.error('Error fetching subscription:', subError);
          }
          setHasActiveSubscription(false);
          setSubscriptionData(null);
          notifyChromeExtension(false);
          return;
        }

        if (!subData) {
          setHasActiveSubscription(false);
          setSubscriptionData(null);
          notifyChromeExtension(false);
          return;
        }

        // Check if subscription is active
        const isActive = subData.status === 'active';

        setHasActiveSubscription(isActive);
        setSubscriptionData(subData);
        notifyChromeExtension(isActive);
      } catch (error) {
        console.error('Error checking subscription:', error);
        setHasActiveSubscription(false);
        setSubscriptionData(null);
      }
      setLoading(false);
    };

    checkSubscription();
  }, [user]);

  const value = {
    hasActiveSubscription,
    loading,
    subscriptionData,
    handleManageSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
